import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, MessageCircle, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const faqs = [
  {
    category: "Getting Started",
    questions: [
      {
        q: "How do I create an account?",
        a: "Visit cyberxcalls.com/portal and click on 'Sign Up'. Fill in your details and verify your email to get started. The process takes just a few minutes."
      },
      {
        q: "What is the minimum recharge amount?",
        a: "The minimum recharge amount is 100 USD or 100 EUR. You can add more credits at any time through your portal."
      },
      {
        q: "What payment methods do you accept?",
        a: "We accept various payment methods including bank transfers and other electronic payment options. Contact our support team for specific payment options available in your region."
      },
      {
        q: "How quickly can I start making calls?",
        a: "Once your account is funded, you can start making calls immediately. SIP credentials are provided in your portal dashboard."
      }
    ]
  },
  {
    category: "Services & Plans",
    questions: [
      {
        q: "What's the difference between Silver, Gold, and Platinum plans?",
        a: "Silver offers competitive pricing without CLI, ideal for price-conscious users. Gold provides quality-focused retail routes with CLI and high ASR/ACD. Platinum offers premium Tier 1 quality with full CLI delivery and direct partnerships."
      },
      {
        q: "Do you offer call center specific routes?",
        a: "Yes! Our Call Center IP Voice routes are specifically engineered for call center traffic with low ACD and high CPM. These routes use carriers with proven tolerance for call center traffic patterns."
      },
      {
        q: "Can I switch between plans?",
        a: "Yes, you can switch between different route quality tiers based on your needs. Contact our support team to discuss your requirements."
      },
      {
        q: "Do you offer volume discounts?",
        a: "Yes, we offer custom pricing for high-volume customers. Contact our sales team on Telegram to discuss volume discounts."
      }
    ]
  },
  {
    category: "Technical",
    questions: [
      {
        q: "What codecs do you support?",
        a: "We support G.711 (PCMU/PCMA), G.729, and other standard VoIP codecs. G.711 is recommended for best quality."
      },
      {
        q: "Do you provide CLI (Caller ID)?",
        a: "CLI delivery depends on the plan. Gold and Platinum plans include CLI delivery, while Silver routes do not guarantee CLI pass-through."
      },
      {
        q: "What's your uptime guarantee?",
        a: "We maintain 99.9% uptime with redundant infrastructure. Our systems are monitored 24/7 to ensure consistent service."
      },
      {
        q: "How often are rates updated?",
        a: "Rates are updated regularly based on carrier changes. You can always download the latest rates from our rates page or via API."
      }
    ]
  },
  {
    category: "Billing & Referral",
    questions: [
      {
        q: "How does billing work?",
        a: "We use prepaid billing. Add credits to your account and calls are deducted in real-time. You can monitor your balance in the portal."
      },
      {
        q: "How does the referral program work?",
        a: "Refer a new customer and earn 50% credit of their first recharge. For example, if they recharge €100, you get €50 credit. There's no limit to referrals."
      },
      {
        q: "When do I receive my referral credit?",
        a: "Referral credits are added to your account within 24 hours after your referral makes their first recharge of at least 100 USD/EUR."
      },
      {
        q: "Can I get a refund?",
        a: "Account credits are non-refundable but never expire. Contact support if you have specific concerns."
      }
    ]
  },
  {
    category: "Support",
    questions: [
      {
        q: "How can I reach support?",
        a: "The fastest way to reach us is via Telegram @cyberx007. You can also email admin@cyberxcalls.com or call +1 (973) 922-3220."
      },
      {
        q: "What are your support hours?",
        a: "Our Telegram support is available 24/7. Email and phone support are available during business hours with responses within 24 hours."
      },
      {
        q: "Do you have a physical office?",
        a: "We operate globally as a remote company with no physical office. All support and services are provided remotely."
      }
    ]
  }
];

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button
        onClick={onClick}
        className="w-full py-5 flex items-center justify-between text-left"
      >
        <span className="font-medium text-slate-900 pr-4">{question}</span>
        <ChevronDown 
          className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-slate-600">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQPage = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div data-testid="faq-page">
      {/* Hero */}
      <section className="bg-slate-900 text-white py-20 noise-bg" data-testid="faq-hero">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 bg-red-600/20 text-red-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <HelpCircle className="w-4 h-4" />
              Help Center
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-4">Frequently Asked Questions</h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Find answers to common questions about our VoIP services, billing, and support.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="section-light" data-testid="faq-content">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            {faqs.map((category, categoryIndex) => (
              <motion.div
                key={categoryIndex}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: categoryIndex * 0.1 }}
                viewport={{ once: true }}
                className="mb-12"
              >
                <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b-2 border-red-500 inline-block">
                  {category.category}
                </h2>
                <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                  {category.questions.map((faq, questionIndex) => (
                    <FAQItem
                      key={questionIndex}
                      question={faq.q}
                      answer={faq.a}
                      isOpen={openItems[`${categoryIndex}-${questionIndex}`]}
                      onClick={() => toggleItem(categoryIndex, questionIndex)}
                    />
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="section-alt" data-testid="faq-contact">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Still Have Questions?</h2>
            <p className="text-slate-600 mb-8 max-w-xl mx-auto">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="https://t.me/cyberx007" target="_blank" rel="noopener noreferrer">
                <Button className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6">
                  <MessageCircle className="mr-2 w-4 h-4" /> Chat on Telegram
                </Button>
              </a>
              <Link to="/contact">
                <Button variant="outline" className="rounded-full px-6 border-slate-300">
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
